import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { api, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

const PLACEHOLDER = "https://images.unsplash.com/photo-1631014304559-24a497774470?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxuYXR1cmFsJTIwbWVkaWNpbmUlMjBib3R0bGUlMjBwbGFudHxlbnwwfHx8fDE3ODIxMTI1NjF8MA&ixlib=rb-4.1.0&q=85";

export default function Cart() {
  const { items, setQty, remove, total } = useCart();
  const nav = useNavigate();
  // Live stock so the cart cannot ask for more than the shelf holds.
  const [stock, setStock] = React.useState({});
  React.useEffect(() => {
    api.get("/products")
      .then(({ data }) => setStock(Object.fromEntries(data.map((p) => [p.id, p.stock]))))
      .catch(() => {});
  }, []);
  const stockOf = (it) => (stock[it.id] === undefined ? null : stock[it.id]);

  if (items.length === 0) return (
    <main className="max-w-3xl mx-auto px-6 py-20 text-center">
      <ShoppingBag size={48} className="mx-auto text-muted-foreground mb-4" />
      <h1 className="font-display text-4xl mb-2">Your cart is empty</h1>
      <p className="text-muted-foreground mb-6">Browse the marketplace to add natural remedies to your cart.</p>
      <Link to="/shop"><Button className="btn-pill bg-primary text-primary-foreground" data-testid="cart-shop-btn">Shop Natural Products</Button></Link>
    </main>
  );

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
      <h1 className="font-display text-4xl mb-8">Your Cart</h1>
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-4 p-4 border border-border rounded-xl" data-testid={`cart-item-${it.id}`}>
              <div className="w-20 h-20 rounded-lg bg-muted overflow-hidden shrink-0">
                <img src={mediaUrl(it.image) || PLACEHOLDER} alt={it.name} className="w-full h-full object-cover" data-testid={`cart-img-${it.id}`}/>
              </div>
              <div className="flex-1">
                <div className="font-display text-xl">{it.name}</div>
                <div className="text-sm text-muted-foreground">₹{it.price.toFixed(2)}</div>
                {stockOf(it) !== null && it.quantity >= stockOf(it) && (
                  <div className="text-[11px] text-secondary mt-0.5" data-testid={`cart-stock-${it.id}`}>
                    {stockOf(it) === 0 ? "Out of stock" : `Only ${stockOf(it)} available`}
                  </div>
                )}
              </div>
              <div className="flex items-center border border-border rounded-full">
                <button onClick={()=>setQty(it.id, it.quantity-1)} className="p-2" data-testid={`cart-dec-${it.id}`}><Minus size={14}/></button>
                <span className="px-3 text-sm">{it.quantity}</span>
                <button
                  onClick={()=>setQty(it.id, it.quantity+1)}
                  disabled={stockOf(it) !== null && it.quantity >= stockOf(it)}
                  className="p-2 disabled:opacity-40"
                  data-testid={`cart-inc-${it.id}`}
                ><Plus size={14}/></button>
              </div>
              <div className="w-24 text-right font-medium">₹{(it.price*it.quantity).toFixed(2)}</div>
              <button onClick={()=>remove(it.id)} className="text-destructive p-2" data-testid={`cart-rm-${it.id}`}><Trash2 size={16}/></button>
            </div>
          ))}
        </div>
        <div className="border border-border rounded-2xl p-6 h-fit">
          <h3 className="font-display text-2xl mb-4">Order Summary</h3>
          <div className="flex justify-between text-sm mb-2"><span>Subtotal</span><span>₹{total.toFixed(2)}</span></div>
          <div className="flex justify-between text-sm mb-2"><span>Shipping</span><span className="text-secondary">Free</span></div>
          <div className="border-t border-border my-3"/>
          <div className="flex justify-between font-display text-2xl mb-6"><span>Total</span><span>₹{total.toFixed(2)}</span></div>
          <Button onClick={()=>nav("/checkout")} className="w-full btn-pill bg-primary text-primary-foreground hover:bg-primary/90 h-11" data-testid="cart-checkout-btn">
            Proceed to Checkout
          </Button>
        </div>
      </div>
    </main>
  );
}
